import React from 'react';
import { useSubscriptionByIdQuery } from '../../services/hooks/useSuscripciones';
import { Link } from 'react-router-dom';
import { Loader2, ExternalLink } from 'lucide-react';
import { useSafeMounted } from '../../services/hooks/useSafeMounted';
import { useTenantCode } from '../../services/hooks/useTenantCode';

interface BeneficiarioInfoProps {
  beneficiarioId: string;
}

const BeneficiarioInfo: React.FC<BeneficiarioInfoProps> = ({ beneficiarioId }) => {
    const isMounted = useSafeMounted();
    const tenantCode = useTenantCode();
    const { data: suscripcionBeneficiario, isLoading, error } = useSubscriptionByIdQuery(
        beneficiarioId,
        {
            // Solo permitir ejecutar si el componente está montado
            enabled: !!beneficiarioId && isMounted.current,
            // No refrescar al perder el foco
            refetchOnWindowFocus: false,
            retry: false,
            staleTime: 5 * 60 * 1000, // 5 minutos
            cacheTime: 60 * 60 * 1000, // 1 hora
            refetchOnMount: false,
            onError: () => {
                // Manejar solo si está montado
                if (!isMounted.current) return;
            }
        }
    );

    // Si el componente está desmontado, no renderizar nada
    if (!isMounted.current) return null;

    if (isLoading) {
        return (
        <div className="flex items-center text-sm text-gray-500">
            <Loader2 size={14} className="mr-2 animate-spin" />
            Cargando datos del beneficiario...
        </div>
        );
    }

    if (error || !suscripcionBeneficiario) {
        return <div className="text-sm text-red-500">Error al cargar datos del beneficiario</div>;
    }

    // Extraer datos del cliente beneficiario de la estructura anidada
    const clienteBeneficiario = suscripcionBeneficiario?.cliente_id;
    
    if (!clienteBeneficiario) {
        // return <div className="text-sm text-red-500">Datos del cliente beneficiario no disponibles</div>;
        return null;
    }

    return (
        <div className="mt-2 p-3 bg-purple-50 rounded-md">
            <div className="flex justify-between items-start">
                <h4 className="font-semibold text-purple-800 mb-1">Beneficiario</h4>
                {/* Las rutas de la app viven bajo /:tenantCode. Sin el prefijo,
                    "clientes" se interpreta como el código de centro y la ficha
                    no carga. */}
                <Link
                    to={`/${tenantCode}/clientes/${clienteBeneficiario._id}`}
                    className="text-purple-700 hover:text-purple-900 text-sm flex items-center"
                >
                    Ver ficha completa <ExternalLink size={14} className="ml-1" />
                </Link>
            </div>
            <p><strong>Nombre:</strong> {clienteBeneficiario.nombre}</p>
            <p><strong>DNI:</strong> {clienteBeneficiario.dni}</p>
            <p><strong>Teléfono:</strong> {clienteBeneficiario.telefono}</p>
            {clienteBeneficiario.email && <p><strong>Email:</strong> {clienteBeneficiario.email}</p>}
        </div>
    );
};

// Usar memo para evitar re-renderizados innecesarios
export default React.memo(BeneficiarioInfo);